/**
 * Pure model + coercion for the dashboard preferences blob.
 *
 * Split out from the React provider (`./dashboardPrefs`) so the shape, the
 * reserved-surface constants, and the untrusted-input coercion are testable
 * without pulling React / the saved-views client into a unit test — and so both
 * the local cache and the server row are normalised through ONE function.
 */
import type {
  InheritedScopeSource,
  ManagedScopeDefaults,
  ReadScopeOverrides,
  WriteScopeOverrides,
} from "@authe/fe-commons/attribution";
import {
  GRAPHQL_MASTER_FLAG,
  flagForOperation,
  isDataOperation,
  type DataOperation,
  type Transport,
} from "@authe/fe-commons/transport";
import type { FlagMap } from "@authe/fe-commons/flags";
import { isTxTenancyScopeId, type TxTenancyScopeId } from "@/features/transactions/scopes";
// The product defaults, from the registry — so "what stands when nobody has
// chosen?" is answered in one place rather than paired with a layer's name by
// each settings row.
import { TRANSPORT_MASTER_DEFAULT, transportDefaultFor } from "@/config/transport";

/** Reserved saved-view surface that stores the dashboard prefs blob. */
export const PREFS_TABLE_ID = "__prefs:dashboard";
/** ScopedStorage key for the local cache of the whole blob (identity-namespaced). */
export const CACHE_KEY = "prefs:dashboard";
/** Stable name for the single prefs row (never shown; the surface is reserved). */
export const PREFS_VIEW_NAME = "dashboard-prefs";

/**
 * The two override maps, under the names the shared `ScopePrefsStore` uses.
 *
 * Aliases rather than declarations: this blob IS the dashboard's implementation
 * of that contract, and a locally-declared map shape is how a stored tier that
 * the shared resolver would reject gets written anyway.
 */
export type WriteScopeMap = WriteScopeOverrides;
export type ResolveScopeMap = ReadScopeOverrides;

/**
 * Which API each data operation reads over, as ONE layer states it.
 *
 * A sparse document on purpose: a key that is absent means "inherit", which is
 * what lets the plan layer speak about the master switch, the workspace layer
 * about two operations, and the user about a third, without any of them having
 * to restate the others. The same shape at every rung — a workspace default is
 * an admin saving the settings they are looking at.
 *
 * `master` mirrors the `graphql.enabled` kill-switch; `operations` is keyed by
 * the registry's {@link DataOperation}, so an operation that no longer exists
 * (one removed from the registry) is dropped on read rather than resurrected as
 * a flag nothing consults.
 */
export interface TransportPrefs {
  /** AppSync subscriptions, independent of the REST/GraphQL read preference. */
  streaming?: boolean;
  /** The master switch, or `null`/absent to inherit the tier below. */
  master?: boolean | null;
  /** Per-operation choice. An absent key inherits. */
  operations?: Partial<Record<DataOperation, Transport>>;
}

export const EMPTY_TRANSPORT: TransportPrefs = {};

/** The serialisable preferences blob (mirrors `view_state.extra` on the row). */
export interface DashboardPrefsBlob {
  writeScope: WriteScopeMap;
  resolveScope: ResolveScopeMap;
  /**
   * The Transactions explorer's default feed partition.
   *
   * ONE field rather than a map keyed by surface, because there is exactly one
   * surface with this axis. The Addresses list looks like a second — it queries
   * `scope: "tenant"` — but that is `AddressListScope` (tenant | entity |
   * cross_chain), a query MODE, and that route takes no tenancy partition at
   * all; `include_global` is its only tenancy toggle and the page already
   * exposes it. A map with one member would be a seam with nothing behind it.
   */
  txFeed: TxTenancyScopeId | null;
  /**
   * REST vs GraphQL, per operation.
   *
   * It lived only on `FeatureFlags`/localStorage, on the reasoning that a
   * setting deciding WHICH API a read uses cannot itself depend on a network
   * read. The first half of that is true and the conclusion does not follow:
   * this store is REST-only (saved-views-api) so there is no circularity, it
   * seeds synchronously from the local cache, and the transport registry has a
   * working default the instant the app boots. What the reasoning actually cost
   * was central management — a per-browser choice no admin could set, no plan
   * could shape, and support could not read back.
   */
  transport: TransportPrefs;
}

export const EMPTY_BLOB: DashboardPrefsBlob = {
  writeScope: {},
  resolveScope: {},
  txFeed: null,
  transport: EMPTY_TRANSPORT,
};

/**
 * True when `id` is a tier this preference may hold.
 *
 * The shared `isTxTenancyScopeId` rather than a check of our own against
 * `TX_SCOPES`: that list also carries `latest`, which names an ENDPOINT (the
 * ephemeral latest-block feed) and not a partition. A stored `latest` would have
 * become "the tenancy tier your reads open at" — a tier the Settings picker never
 * offers, that cannot be resolved to widen params, and that the scope pill would
 * have reported as the case's partition.
 */
export const isKnownTxScope = isTxTenancyScopeId;

/**
 * Coerce an untrusted (local-cache / server) value into a well-formed blob. The
 * store never trusts either source's shape: non-object maps collapse to `{}`
 * and an unknown/absent tx-feed scope collapses to `null` (so a stale scope the
 * ladder dropped can't pin the explorer to a tier that no longer exists).
 */
export function normalizeBlob(raw: unknown): DashboardPrefsBlob {
  const r = (raw ?? {}) as Partial<DashboardPrefsBlob>;
  const writeScope =
    r.writeScope && typeof r.writeScope === "object" ? (r.writeScope as WriteScopeMap) : {};
  const resolveScope =
    r.resolveScope && typeof r.resolveScope === "object"
      ? (r.resolveScope as ResolveScopeMap)
      : {};
  return {
    writeScope,
    resolveScope,
    txFeed: isKnownTxScope(r.txFeed) ? r.txFeed : null,
    transport: normalizeTransport(r.transport),
  };
}

/**
 * Coerce an untrusted transport document.
 *
 * Every key is checked against the live registry, not merely against `string`:
 * a stored operation the registry has since dropped, or a transport spelled
 * anything but `rest`/`graphql`, would otherwise become a `graphql.<something>`
 * flag that no resolver reads and no settings row can clear — invisible state
 * that survives every "reset to default" because nothing knows it is there.
 */
export function normalizeTransport(raw: unknown): TransportPrefs {
  const r = (raw ?? {}) as Partial<TransportPrefs>;
  const out: TransportPrefs = {};
  if (typeof r.master === "boolean") out.master = r.master;
  if (typeof r.streaming === "boolean") out.streaming = r.streaming;

  const ops = r.operations;
  if (ops && typeof ops === "object") {
    const operations: Partial<Record<DataOperation, Transport>> = {};
    for (const [op, transport] of Object.entries(ops)) {
      if (!isDataOperation(op)) continue;
      if (transport !== "rest" && transport !== "graphql") continue;
      operations[op] = transport;
    }
    if (Object.keys(operations).length > 0) out.operations = operations;
  }
  return out;
}


/**
 * The parts of this blob that are published INDEPENDENTLY.
 *
 * There is one saved-view row per layer, so publishing the workspace default
 * used to replace `view_state` wholesale with the publishing admin's own blob.
 * That is wrong in a way nobody would notice until it mattered: admin A
 * publishes overlay-visibility defaults; admin B — whose own blob says nothing
 * about those, because they INHERITED A's — changes one API preference and
 * clicks "Update the workspace default"; everyone in the tenant silently reverts
 * to the product default for "who can see this label". The evidentiary setting
 * this store exists to make central is the one that gets withdrawn.
 *
 * Merging the caller's blob onto the row would fix that and cost the ability to
 * withdraw anything: a published value could never be taken back, only replaced.
 * So the unit of publication is a SECTION — the group of settings one card
 * governs. Publishing a section replaces that section and leaves the others
 * exactly as they were; clearing one empties that section alone; and a row left
 * stating nothing is deleted, so "no workspace default is published" stays a
 * reachable state (the hydrate reads an absent row as a real answer).
 *
 * Two sections because there are two cards: Settings › tenancy defaults, and
 * Settings › APIs.
 */
export type DashboardPrefsSection = "scopes" | "transport";

/** Which fields each section owns — the one place that mapping is written. */
const SECTION_FIELDS: Record<DashboardPrefsSection, readonly (keyof DashboardPrefsBlob)[]> = {
  scopes: ["writeScope", "resolveScope", "txFeed"],
  transport: ["transport"],
};

/** True when `field` states nothing at all. */
function fieldIsEmpty(blob: DashboardPrefsBlob, field: keyof DashboardPrefsBlob): boolean {
  if (field === "txFeed") return !blob.txFeed;
  if (field === "transport") {
    const transport = blob.transport;
    return transport.master === undefined && transport.streaming === undefined && Object.keys(transport.operations ?? {}).length === 0;
  }
  return Object.keys(blob[field]).length === 0;
}

/**
 * True when a section of `blob` carries nothing — the shape "inherit
 * everything" takes, and what a publish button must refuse to publish.
 *
 * Asked of the SECTION rather than the whole blob, because the surface that
 * inspected the whole thing got it wrong in both directions: Settings' "remove
 * the workspace default" was gated on a check that looked at the two scope maps
 * and the tx feed and ignored `transport` entirely, so a transport-only
 * workspace default could never be withdrawn — one card said a default was
 * published while the other said none was.
 */
export function sectionIsEmpty(blob: DashboardPrefsBlob, section: DashboardPrefsSection): boolean {
  return SECTION_FIELDS[section].every((field) => fieldIsEmpty(blob, field));
}

/** True when nothing in the blob states anything (every section empty). */
export function blobIsEmpty(blob: DashboardPrefsBlob): boolean {
  return (Object.keys(SECTION_FIELDS) as DashboardPrefsSection[]).every((section) =>
    sectionIsEmpty(blob, section),
  );
}

/**
 * `base` with one section taken from `next` — or emptied, when `next` is null.
 *
 * The other sections come through untouched, which is the whole point: an admin
 * publishing what they are looking at must not withdraw what somebody else
 * published on another card.
 */
export function withSection(
  base: DashboardPrefsBlob,
  section: DashboardPrefsSection,
  next: DashboardPrefsBlob | null,
): DashboardPrefsBlob {
  const source = next ?? EMPTY_BLOB;
  const merged: DashboardPrefsBlob = { ...base };
  for (const field of SECTION_FIELDS[section]) {
    // Per field, so a section is a set of whole values rather than a deep
    // merge: publishing "the tenancy defaults I am looking at" means exactly
    // the maps on screen, including the entries the admin cleared.
    (merged as unknown as Record<string, unknown>)[field] = source[field];
  }
  return merged;
}

/**
 * One centrally-published layer, as this app stores and caches it.
 *
 * A saved-view row per layer — `tenant` scope for the workspace default,
 * `system` for a fleet-wide one — carrying the SAME blob shape as a user's own.
 * Same shape on purpose: publishing a workspace default is the admin saving the
 * settings they are looking at, and a second shape for "the same settings, for
 * everyone" is a second thing to keep in step.
 */
export interface ManagedPrefsLayer {
  source: InheritedScopeSource;
  /** The saved-view row id, when one exists (absent until first published). */
  viewId?: string;
  blob: DashboardPrefsBlob;
}

/**
 * Which saved-view scope backs which layer, most general FIRST.
 *
 * The order is the resolution order, so it is written once here rather than
 * implied by the order of two `find` calls. `system` is internal-only to write
 * (`SavedViewService._guard_scope`) and readable fleet-wide, which is exactly
 * the shape of a per-plan default: ours to publish, everyone's to inherit.
 */
export const MANAGED_PREFS_LAYERS: readonly { scope: "system" | "tenant"; source: InheritedScopeSource }[] = [
  { scope: "system", source: "plan" },
  { scope: "tenant", source: "workspace" },
];

/** The whole local cache: the caller's own row plus the layers beneath it. */
export interface DashboardPrefsCache {
  own: DashboardPrefsBlob;
  managed: ManagedPrefsLayer[];
}

export const EMPTY_CACHE: DashboardPrefsCache = { own: EMPTY_BLOB, managed: [] };

/**
 * Coerce an untrusted cache entry into layers.
 *
 * Tolerates the pre-layer shape — a bare blob written by an earlier build —
 * by reading it as the caller's own row, which is what it was. Dropping it
 * instead would silently reset everyone's saved defaults on the deploy that
 * introduced layering, and a preference that resets itself is indistinguishable
 * from one that never saved.
 */
export function normalizeCache(raw: unknown): DashboardPrefsCache {
  const r = (raw ?? {}) as Partial<DashboardPrefsCache>;
  if (!r.own && !r.managed) return { own: normalizeBlob(raw), managed: [] };
  return {
    own: normalizeBlob(r.own),
    managed: Array.isArray(r.managed)
      ? r.managed
          .filter((layer): layer is ManagedPrefsLayer => Boolean(layer) && typeof layer === "object")
          .map((layer) => ({
            source: layer.source,
            ...(layer.viewId ? { viewId: layer.viewId } : {}),
            blob: normalizeBlob(layer.blob),
          }))
      : [],
  };
}

/**
 * The layers in the shape the shared resolver reads, least specific first.
 *
 * The translation is one line and it is here rather than in the provider so the
 * provider never has to know that `ScopePrefsStore.managed` is ordered — the one
 * fact about the shared contract that a caller can get wrong without a type
 * error, and get wrong quietly (the wrong layer wins).
 */
export function managedScopeDefaults(
  layers: readonly ManagedPrefsLayer[],
): ManagedScopeDefaults[] {
  return layers.map((layer) => ({
    source: layer.source,
    writeScope: layer.blob.writeScope,
    resolveScope: layer.blob.resolveScope,
  }));
}

/** The first managed layer that names a transactions feed default, most specific first. */
export function managedTxFeed(
  layers: readonly ManagedPrefsLayer[],
): TxTenancyScopeId | null {
  for (let i = layers.length - 1; i >= 0; i -= 1) {
    const scope = layers[i]?.blob.txFeed;
    if (scope) return scope;
  }
  return null;
}

/**
 * Collapse the transport layers into the `FlagMap` the flag store's POLICY layer
 * takes — least specific first, so the caller's own row wins.
 *
 * A MERGE, unlike {@link managedTxFeed}'s pick, because these layers speak about
 * different operations rather than about the same one: a plan that sets the
 * master switch and a user who forced one read onto REST are both right, and a
 * pick would silently discard whichever lost. Keys absent everywhere stay absent
 * — the flag store reads that as "no opinion" and the registry default stands,
 * which is what makes the product default reachable again after a reset.
 */
export function transportPolicyFlags(
  layers: readonly ManagedPrefsLayer[],
  own: TransportPrefs,
): FlagMap {
  const map: FlagMap = {};
  const apply = (prefs: TransportPrefs | undefined): void => {
    if (!prefs) return;
    if (typeof prefs.master === "boolean") map[GRAPHQL_MASTER_FLAG] = prefs.master;
    for (const [op, transport] of Object.entries(prefs.operations ?? {})) {
      map[flagForOperation(op as DataOperation)] = transport === "graphql";
    }
  };
  for (const layer of layers) apply(layer.blob.transport);
  apply(own);
  return map;
}

/**
 * The most specific layer that states `op`, and the transport it states.
 *
 * Both halves together, because a row needs both and pairing them by hand is
 * how they came apart: the settings row labelled its "Default (…)" option with
 * the REGISTRY's value while attributing it to the layer's name, so a row read
 * "Default (REST — set by your workspace)" when the workspace had published
 * GraphQL. The name and the value have to come from the same answer.
 */
export function inheritedTransportFor(
  layers: readonly ManagedPrefsLayer[],
  op: DataOperation,
): { source: InheritedScopeSource; transport: Transport } | null {
  for (let i = layers.length - 1; i >= 0; i -= 1) {
    const layer = layers[i];
    const stated = layer?.blob.transport.operations?.[op];
    if (layer && stated !== undefined) return { source: layer.source, transport: stated };
  }
  return null;
}

/** The most specific layer that states the master switch, and what it says. */
export function inheritedTransportMaster(
  layers: readonly ManagedPrefsLayer[],
): { source: InheritedScopeSource; master: boolean } | null {
  for (let i = layers.length - 1; i >= 0; i -= 1) {
    const layer = layers[i];
    const stated = layer?.blob.transport.master;
    if (layer && typeof stated === "boolean") return { source: layer.source, master: stated };
  }
  return null;
}

/**
 * The most specific layer that states something about `op` (or the master
 * switch when `op` is omitted), or `null` when none does.
 *
 * What a settings row needs in order to say "inherited from your workspace"
 * rather than showing an inherited value as if the reader had chosen it — the
 * exact confusion the layered store was built to end.
 */
export function transportLayerSource(
  layers: readonly ManagedPrefsLayer[],
  op?: DataOperation,
): InheritedScopeSource | null {
  const inherited = op ? inheritedTransportFor(layers, op) : inheritedTransportMaster(layers);
  return inherited?.source ?? null;
}

/**
 * What stands for `op` when this caller chooses nothing — the value AND whose
 * choice it is, with the product default as the floor.
 *
 * The one answer a "Default (…)" label should be built from. `source: null`
 * means only the product default speaks, so the label names no owner.
 */
export function transportFallback(
  layers: readonly ManagedPrefsLayer[],
  op: DataOperation,
): { transport: Transport; source: InheritedScopeSource | null } {
  const inherited = inheritedTransportFor(layers, op);
  return inherited ?? { transport: transportDefaultFor(op), source: null };
}

/** The same, for the master switch. */
export function transportMasterFallback(
  layers: readonly ManagedPrefsLayer[],
): { master: boolean; source: InheritedScopeSource | null } {
  const inherited = inheritedTransportMaster(layers);
  return inherited ?? { master: TRANSPORT_MASTER_DEFAULT, source: null };
}
